import { type ComputedRef, computed, getCurrentScope, inject, type Ref, watch } from 'vue'
import { routeLocationKey } from 'vue-router'
import { sessionEnterpriseId } from '@/lib/sessionEnterprise'

/** What a list page can keep: free text, a picked value, a switch, a number, or a map of filters. */
export type ListValue = string | number | boolean | Record<string, string>

/** The values a picked-from-list field may still hold; `null` while the options are loading (keep the value). */
export type ListAllowed = () => readonly string[] | null

/** One saved choice on a list page: the page's own ref, and how the URL and the loaded options relate to it. */
export type ListField = {
    ref: Ref<ListValue>
    /** The URL query key that sets this scalar field for one visit (`?status=failed`). */
    query?: string
    /** For a map of filters: the URL query keys that set the entry of the same name (`?taskId=t1`). */
    queryKeys?: readonly string[]
    /** Checks a picked value (a team id, a person id) against the page's loaded options. */
    allowed?: ListAllowed
    /**
     * Which list the page shows (Catalog's Teams / Roles / Tasks), not a filter on it: it is saved and restored like
     * any other choice, but Clear filters leaves it, and it never makes the list read as narrowed.
     */
    keep?: boolean
}

export type ListStateOptions = {
    /** `enterprise` (default): kept per enterprise. `person`: for lists that span enterprises. */
    scope?: 'enterprise' | 'person'
}

/** What a page hands `DataTable` (or its own Clear filters button): whether the list is narrowed, and how to clear it. */
export type ListState = { clear: () => void; isDefault: ComputedRef<boolean> }

/**
 * What `useListState` gives back: the list state, and `rekey` for a page its route reuses for another item
 * (`/teams/t1` → `/teams/t2`, no remount), so that item's list keeps its own choices.
 */
export type KeyedListState = ListState & { rekey: (key: string) => void }

const PREFIX = 'os:list:'
const VERSION = 1
/** A shared link's free text is read up to this length, as the pages that read it did before. */
const URL_TEXT_MAX = 200

type Values = Record<string, unknown>

/** The browser's storage, or null where a private window, blocked site data or the test runner offers none. */
function storage(): Storage | null {
    try {
        const store = globalThis.localStorage
        return store && typeof store.getItem === 'function' ? store : null
    } catch {
        return null
    }
}

function isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function isMap(value: ListValue): value is Record<string, string> {
    return typeof value === 'object'
}

function copy(value: ListValue): ListValue {
    return isMap(value) ? { ...value } : value
}

/** The saved values, or null for nothing saved, corrupt JSON, a non-object payload or a version this build can't read. */
function readSaved(key: string): Values | null {
    try {
        const raw = storage()?.getItem(key)
        if (!raw) {
            return null
        }
        const parsed: unknown = JSON.parse(raw)
        if (!isRecord(parsed) || parsed.version !== VERSION || !isRecord(parsed.values)) {
            return null
        }
        return { ...parsed.values }
    } catch {
        return null
    }
}

function writeSaved(key: string, values: Values): void {
    try {
        storage()?.setItem(key, JSON.stringify({ version: VERSION, values }))
    } catch {
        // No storage: the choices last only as long as the page does.
    }
}

function removeSaved(key: string): void {
    try {
        storage()?.removeItem(key)
    } catch {
        // Nothing could be stored in the first place.
    }
}

/** A saved value of the default's own type, or undefined when it is of another (the field keeps its default). */
function ofType(saved: unknown, fallback: ListValue): ListValue | undefined {
    if (isMap(fallback)) {
        if (!isRecord(saved)) {
            return undefined
        }
        return Object.fromEntries(
            Object.entries(saved).filter((entry): entry is [string, string] => typeof entry[1] === 'string'),
        )
    }
    if (typeof fallback === 'number') {
        return typeof saved === 'number' && Number.isFinite(saved) ? saved : undefined
    }
    return typeof saved === typeof fallback ? (saved as ListValue) : undefined
}

/** One URL query value read as the field's type, or undefined when it is absent or can't be. */
function fromQuery(raw: unknown, fallback: ListValue): ListValue | undefined {
    const text = Array.isArray(raw) ? raw[0] : raw
    if (typeof text !== 'string') {
        return undefined
    }
    if (typeof fallback === 'boolean') {
        if (text === 'true' || text === '1') {
            return true
        }
        return text === 'false' || text === '0' ? false : undefined
    }
    if (typeof fallback === 'number') {
        const value = Number(text)
        return text.trim() !== '' && Number.isFinite(value) ? value : undefined
    }
    return typeof fallback === 'string' ? text.slice(0, URL_TEXT_MAX) : undefined
}

/** Two values are the same choice; an empty entry in a map of filters counts as no entry. */
function same(a: ListValue, b: ListValue): boolean {
    if (isMap(a) && isMap(b)) {
        const left = Object.entries(a).filter(([, value]) => value !== '')
        const right = Object.entries(b).filter(([, value]) => value !== '')
        return left.length === right.length && left.every(([name, value]) => b[name] === value)
    }
    return a === b
}

/** A picked value the loaded options no longer hold; nothing is stale while the options load. */
function isStale(value: ListValue, fallback: ListValue, allowed: readonly string[] | null): boolean {
    if (allowed === null || same(value, fallback)) {
        return false
    }
    if (isMap(value)) {
        return Object.values(value).some(entry => entry !== '' && !allowed.includes(entry))
    }
    return !allowed.includes(String(value))
}

function storageKey(key: string, options?: ListStateOptions): string | null {
    if (options?.scope === 'person') {
        return `${PREFIX}person:${key}`
    }
    const enterpriseId = sessionEnterpriseId()
    return enterpriseId ? `${PREFIX}${enterpriseId}:${key}` : null
}

/**
 * Keep a list page's search, filters, sort, tab and mine/everyone choices in the browser under `key`, restored at
 * setup before the page's first load. `isDefault` is false whenever the choices differ from what the page started
 * with; `clear()` puts them all back and removes the saved entry.
 *
 * - Kept per enterprise (`os:list:<enterpriseId>:<key>`), read once at setup: switching enterprise is a full reload.
 *   With no enterprise known nothing is read or written. `scope: 'person'` is for lists that span enterprises.
 * - A URL value (`query`, `queryKeys`) wins for the visit and is not saved unless the person changes a choice then.
 *   Restoring never rewrites the URL.
 * - A saved value of the wrong type takes its default. A picked value checked by `allowed` is kept while the options
 *   load and dropped, with the saved entry rewritten, once they have loaded without it.
 * - Storage that can't be used means the choices last only as long as the page does.
 * - A page its route reuses for another item calls `rekey` with that item's key: see `rekey` below.
 */
export function useListState(
    key: string,
    fields: Record<string, ListField>,
    options?: ListStateOptions,
): KeyedListState {
    let listKey = key
    let savedKey = storageKey(key, options)
    const scope = getCurrentScope()
    const route = inject(routeLocationKey, null)
    const query = route?.query ?? {}
    const entries = Object.entries(fields)
    const defaults = new Map(entries.map(([name, field]) => [name, copy(field.ref.value)]))
    const defaultOf = (name: string): ListValue => defaults.get(name) ?? ''

    /** What the browser holds for this list: the payload a drop rewrites until the person changes something. */
    let stored: Values | null = savedKey ? readSaved(savedKey) : null
    /** True once the person changed a choice this visit: from then on, the whole current state is what is saved. */
    let changed = false
    let quiet = false

    function snapshot(): Values {
        return Object.fromEntries(entries.map(([name, field]) => [name, copy(field.ref.value)]))
    }

    function save(): void {
        if (savedKey) {
            writeSaved(savedKey, snapshot())
        }
    }

    /** Runs `change` without the watcher below counting it as the person's choice. */
    function quietly(change: () => void): void {
        quiet = true
        try {
            change()
        } finally {
            quiet = false
        }
    }

    function assign(field: ListField, value: ListValue): void {
        quietly(() => {
            field.ref.value = copy(value)
        })
    }

    const restored = new Map<string, ListValue>()
    for (const [name, field] of entries) {
        const fallback = defaultOf(name)
        let value = fallback
        const fromStore = stored && name in stored ? ofType(stored[name], fallback) : undefined
        if (fromStore !== undefined) {
            value = fromStore
            restored.set(name, fromStore)
        }
        if (field.query) {
            const fromUrl = fromQuery(query[field.query], fallback)
            const offered = field.allowed?.() ?? null
            if (fromUrl !== undefined && !isStale(fromUrl, fallback, offered)) {
                value = fromUrl
                restored.delete(name)
            }
        }
        if (field.queryKeys && isMap(value)) {
            const fromUrl: Record<string, string> = {}
            for (const queryKey of field.queryKeys) {
                const entry = fromQuery(query[queryKey], '')
                if (typeof entry === 'string') {
                    fromUrl[queryKey] = entry
                }
            }
            if (Object.keys(fromUrl).length > 0) {
                value = { ...value, ...fromUrl }
                restored.delete(name)
            }
        }
        field.ref.value = copy(value)
    }

    /**
     * Drops a restored value the options no longer hold (for a map of filters, only its entries they no longer hold),
     * and rewrites the saved entry without it.
     */
    function drop(name: string, field: ListField, loaded: readonly string[]): void {
        const value = field.ref.value
        const next = isMap(value)
            ? Object.fromEntries(Object.entries(value).filter(([, entry]) => entry === '' || loaded.includes(entry)))
            : defaultOf(name)
        assign(field, next)
        if (!savedKey) {
            return
        }
        if (changed) {
            save()
            return
        }
        if (stored && name in stored) {
            if (isMap(next)) {
                stored[name] = next
            } else {
                delete stored[name]
            }
            writeSaved(savedKey, stored)
        }
    }

    /** Checks each restored value a field's `allowed` covers: now, or once the options have loaded. */
    function checkRestored(values: Map<string, ListValue>): void {
        for (const [name, field] of entries) {
            const value = values.get(name)
            const allowed = field.allowed
            if (value === undefined || !allowed) {
                continue
            }
            const check = (loaded: readonly string[] | null): boolean => {
                if (loaded === null) {
                    return false
                }
                if (same(field.ref.value, value) && isStale(value, defaultOf(name), loaded)) {
                    drop(name, field, loaded)
                }
                return true
            }
            if (!check(allowed())) {
                const stop = watch(allowed, loaded => {
                    if (check(loaded)) {
                        stop()
                    }
                })
            }
        }
    }

    checkRestored(restored)

    watch(
        snapshot,
        () => {
            if (quiet) {
                return
            }
            changed = true
            stored = null
            save()
        },
        { deep: true, flush: 'sync' },
    )

    const isDefault = computed(() =>
        entries.every(([name, field]) => field.keep === true || same(field.ref.value, defaultOf(name))),
    )

    function clear(): void {
        quietly(() => {
            for (const [name, field] of entries) {
                if (!field.keep) {
                    field.ref.value = copy(defaultOf(name))
                }
            }
        })
        stored = null
        changed = false
        if (!savedKey) {
            return
        }
        // What is kept (the list being shown) stays saved; with nothing kept away from its default, the entry goes.
        const kept = entries.filter(([name, field]) => field.keep === true && !same(field.ref.value, defaultOf(name)))
        if (kept.length > 0) {
            writeSaved(savedKey, Object.fromEntries(kept.map(([name, field]) => [name, copy(field.ref.value)])))
        } else {
            removeSaved(savedKey)
        }
    }

    /** Puts each field's saved value in place, quietly: its default where none is saved or it is of the wrong type. */
    function putSaved(from: Values | null): Map<string, ListValue> {
        const values = new Map<string, ListValue>()
        quietly(() => {
            for (const [name, field] of entries) {
                const fallback = defaultOf(name)
                const fromStore = from && name in from ? ofType(from[name], fallback) : undefined
                if (fromStore !== undefined) {
                    values.set(name, fromStore)
                }
                field.ref.value = copy(fromStore ?? fallback)
            }
        })
        return values
    }

    /**
     * The page now shows another item's list (its route reused it: `/teams/t1` → `/teams/t2`), kept under `next`.
     * That item's saved choices are put in place, or the defaults when it has none, the way setup restores them, and
     * nothing is written until the person changes one. The URL is not read again: a link's choices were for the visit
     * it opened. The previous item's saved choices are left as they are.
     */
    function rekey(next: string): void {
        if (next === listKey) {
            return
        }
        listKey = next
        savedKey = storageKey(next, options)
        stored = savedKey ? readSaved(savedKey) : null
        changed = false
        const values = putSaved(stored)
        // A check that waits for options belongs to the page, so it stops with it.
        if (scope) {
            scope.run(() => checkRestored(values))
        } else {
            checkRestored(values)
        }
    }

    return { clear, isDefault, rekey }
}
